import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { GetAuditResponseSchema, type GetAuditQuery, type GetAuditResponse } from '@magimp/shared';
import { useApiClient } from '../api/context.js';

export type AuditQueryFilter = Omit<GetAuditQuery, 'cursor' | 'limit'> & { limit?: number };

/**
 * @aiPurpose cursor-paginated TanStack Query hook for the admin audit feed
 *   `GET /api/audit`.
 * @aiInvariant `nextCursor` from each page becomes the cursor parameter for
 *   the next; pagination ends when the server returns `nextCursor: null`.
 *   Filter changes invalidate the cached pages — the query key carries the
 *   serialized filter shape, so a new filter starts a fresh paginated walk.
 */
export function useAuditQuery(filter: AuditQueryFilter = {}) {
  const client = useApiClient();
  return useInfiniteQuery<
    GetAuditResponse,
    Error,
    InfiniteData<GetAuditResponse>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: ['audit', 'list', filter],
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) => {
      const query: Record<string, string | number | boolean | null | undefined> = {
        ...(filter as Record<string, string | number | boolean | null | undefined>),
      };
      if (pageParam) query.cursor = pageParam;
      return client.get<GetAuditResponse>('/audit', { signal, query }, GetAuditResponseSchema);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
